import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
} from 'three';
import { createCanvas, finishTexture } from './theme.js';
import { addStaticCollider } from './util.js';

/**
 * Overhead directional gantry signs.
 *
 * Modelled on the cantilever sign that hangs over Bashir Elnifidi Street where
 * it meets Al-Mashtal Street: a single steel post on the median carrying a
 * horizontal beam, with three backlit blue panels — Aljuref West to the left,
 * Abed Allah Altayeb Street straight on, Almashtal Street to the right. Each
 * panel carries the Arabic name over the Latin one with a pair of white arrows
 * underneath, exactly as in the reference.
 *
 * One template is built and cloned, so all three gantries share the same
 * geometry, materials and panel textures — only the transforms differ.
 */

/** Distance along the street: near the south junction, mid-street, near Al-Mashtal. */
const SITES = [169, 2663, 5100];

const PANELS = [
  { arabic: 'الجريف غرب', latin: 'Aljuref West', arrow: 'left' },
  {
    arabic: 'شارع عبد الله الطيب',
    latin: 'Abed Allah Altayeb Street',
    arrow: 'down',
  },
  { arabic: 'شارع المشتل', latin: 'Almashtal Street', arrow: 'right' },
];

const POST_X = 1.45;
const POST_HEIGHT = 8.15;
const BEAM_Y = 7.72;
const PANEL_WIDTH = 2.9;
const PANEL_HEIGHT = 2.1;
const PANEL_GAP = 0.1;
const PANEL_BOTTOM = 5.4;
const PANEL_START_X = 1.85;

const ARM_START = POST_X - 0.2;
const ARM_END = PANEL_START_X + PANELS.length * PANEL_WIDTH + (PANELS.length - 1) * PANEL_GAP + 0.2;

const ARABIC_FONT = '"Geeza Pro", "Al Nile", "Noto Naskh Arabic", "Times New Roman", serif';
const LATIN_FONT = '"Avenir Next", "Helvetica Neue", Arial, sans-serif';

export function buildGantrySigns(level, route) {
  const materials = createSignMaterials();
  const template = buildGantryTemplate(materials);
  const gantries = [];

  for (const s of SITES) {
    const frame = route.frameAt(s);
    const gantry = template.clone(true);
    gantry.position.copy(frame.position);
    gantry.rotation.y = frame.yaw;
    gantry.name = `gantry-sign-${Math.round(s)}`;
    level.root.add(gantry);

    // Only the post is reachable: the panels clear the tallest traffic.
    const post = frame.position.clone().addScaledVector(frame.right, POST_X);
    addStaticCollider(level, {
      x: post.x,
      y: POST_HEIGHT / 2,
      z: post.z,
      width: 0.66,
      height: POST_HEIGHT,
      depth: 0.66,
      yaw: frame.yaw,
    });

    gantries.push({ s, group: gantry });
  }

  return { gantries, panels: PANELS.length, sites: SITES };
}

function createSignMaterials() {
  return {
    steel: new MeshStandardMaterial({
      color: 0x9aa0a3,
      roughness: 0.48,
      metalness: 0.62,
    }),
    steelDark: new MeshStandardMaterial({
      color: 0x5c6266,
      roughness: 0.55,
      metalness: 0.55,
    }),
    concrete: new MeshStandardMaterial({
      color: 0xb3aa98,
      roughness: 0.94,
      metalness: 0.02,
    }),
    back: new MeshStandardMaterial({
      color: 0x4b5157,
      roughness: 0.62,
      metalness: 0.35,
    }),
  };
}

function buildGantryTemplate(materials) {
  const group = new Group();

  const add = (geometry, material, x, y, z, rotationZ = 0) => {
    const mesh = new Mesh(geometry, material);
    mesh.position.set(x, y, z);
    mesh.rotation.z = rotationZ;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    return mesh;
  };

  // Post on the median, with a concrete plinth and a steel base plate.
  add(
    new CylinderGeometry(0.19, 0.25, POST_HEIGHT, 14),
    materials.steel,
    POST_X,
    POST_HEIGHT / 2,
    0,
  );
  add(new BoxGeometry(1.05, 0.42, 1.05), materials.concrete, POST_X, 0.21, 0);
  add(new BoxGeometry(0.72, 0.09, 0.72), materials.steelDark, POST_X, 0.46, 0);

  // Cantilever beam over the carriageway plus the rail the panels sit on.
  const armLength = ARM_END - ARM_START;
  const armCentre = (ARM_START + ARM_END) / 2;
  add(new BoxGeometry(armLength, 0.3, 0.26), materials.steel, armCentre, BEAM_Y, 0);
  add(
    new BoxGeometry(armLength, 0.15, 0.2),
    materials.steelDark,
    armCentre,
    PANEL_BOTTOM - 0.1,
    0,
  );

  // Diagonal brace from the post up to the beam.
  const braceFrom = { x: POST_X, y: 6.1 };
  const braceTo = { x: POST_X + 2.9, y: BEAM_Y };
  const braceLength = Math.hypot(braceTo.x - braceFrom.x, braceTo.y - braceFrom.y);
  add(
    new BoxGeometry(braceLength, 0.11, 0.14),
    materials.steel,
    (braceFrom.x + braceTo.x) / 2,
    (braceFrom.y + braceTo.y) / 2,
    0,
    Math.atan2(braceTo.y - braceFrom.y, braceTo.x - braceFrom.x),
  );

  // Hangers between the panels, holding the beam and the lower rail apart.
  for (let i = 1; i < PANELS.length; i += 1) {
    const x = PANEL_START_X + i * (PANEL_WIDTH + PANEL_GAP) - PANEL_GAP / 2;
    add(
      new BoxGeometry(0.12, BEAM_Y - PANEL_BOTTOM + 0.2, 0.2),
      materials.steelDark,
      x,
      (BEAM_Y + PANEL_BOTTOM) / 2,
      0,
    );
  }

  // The three panels.
  PANELS.forEach((panel, index) => {
    const centreX =
      PANEL_START_X + index * (PANEL_WIDTH + PANEL_GAP) + PANEL_WIDTH / 2;
    const centreY = PANEL_BOTTOM + PANEL_HEIGHT / 2;
    const face = new MeshBasicMaterial({
      map: createPanelTexture(panel),
      toneMapped: false,
    });
    const mesh = new Mesh(
      new BoxGeometry(PANEL_WIDTH, PANEL_HEIGHT, 0.1),
      [
        materials.steel,
        materials.steel,
        materials.steel,
        materials.steel,
        face,
        materials.back,
      ],
    );
    mesh.position.set(centreX, centreY, 0);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  });

  return group;
}

/**
 * One blue direction panel: Arabic name, Latin name, and a pair of arrows.
 * The canvas is drawn at 1024 × 741 to match the 2.9 × 2.1 m face.
 */
function createPanelTexture({ arabic, latin, arrow }) {
  const width = 1024;
  const height = 741;
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');

  const backdrop = ctx.createLinearGradient(0, 0, 0, height);
  backdrop.addColorStop(0, '#2260b6');
  backdrop.addColorStop(0.55, '#1c50a2');
  backdrop.addColorStop(1, '#153f85');
  ctx.fillStyle = backdrop;
  ctx.fillRect(0, 0, width, height);

  // A faint inner shade, so the flat face still reads as a lit box.
  const shade = ctx.createRadialGradient(
    width * 0.5,
    height * 0.35,
    height * 0.15,
    width * 0.5,
    height * 0.5,
    width * 0.62,
  );
  shade.addColorStop(0, 'rgba(255, 255, 255, 0.09)');
  shade.addColorStop(1, 'rgba(4, 18, 46, 0.22)');
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.lineWidth = 6;
  ctx.strokeRect(14, 14, width - 28, height - 28);

  ctx.fillStyle = '#ffffff';
  drawFittedText(ctx, arabic, {
    maxWidth: width * 0.85,
    fontSize: Math.round(height * 0.19),
    weight: 700,
    font: ARABIC_FONT,
    x: width / 2,
    y: height * 0.235,
    rtl: true,
  });
  drawFittedText(ctx, latin, {
    maxWidth: width * 0.86,
    fontSize: Math.round(height * 0.088),
    weight: 700,
    font: LATIN_FONT,
    x: width / 2,
    y: height * 0.435,
    rtl: false,
  });

  const arrowSize = height * 0.175;
  for (const offset of [-0.2, 0.2]) {
    drawArrow(ctx, width * (0.5 + offset), height * 0.73, arrowSize, arrow);
  }

  return finishTexture(canvas, 8);
}

function drawFittedText(ctx, text, { maxWidth, fontSize, weight, font, x, y, rtl }) {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.direction = rtl ? 'rtl' : 'ltr';
  let size = fontSize;
  ctx.font = `${weight} ${size}px ${font}`;
  const measured = ctx.measureText(text).width;
  if (measured > maxWidth) {
    size = Math.max(12, Math.floor(size * (maxWidth / measured)));
    ctx.font = `${weight} ${size}px ${font}`;
  }
  ctx.fillText(text, x, y);
  ctx.restore();
}

/** Chunky white arrow, drawn pointing right and rotated into place. */
function drawArrow(ctx, cx, cy, size, direction) {
  const half = size / 2;
  const thickness = size * 0.38;
  const headLength = size * 0.44;
  const shaftEnd = -half + (size - headLength);

  ctx.save();
  ctx.translate(cx, cy);
  if (direction === 'down') ctx.rotate(Math.PI / 2);
  else if (direction === 'left') ctx.rotate(Math.PI);

  ctx.beginPath();
  ctx.moveTo(-half, -thickness / 2);
  ctx.lineTo(shaftEnd, -thickness / 2);
  ctx.lineTo(shaftEnd, -half);
  ctx.lineTo(half, 0);
  ctx.lineTo(shaftEnd, half);
  ctx.lineTo(shaftEnd, thickness / 2);
  ctx.lineTo(-half, thickness / 2);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}
